<script setup lang="ts">
import { useRecordPageContext } from '@/composables/recordPageContext';
import { computed } from 'vue';
import SearchResultFieldSections from '@/components/DataTable/SearchResultFieldSections.vue';
import { groupColumnsForInspector } from '@/composables/searchInspectorFieldGroups';
import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * Generic detail pane for the search page's master-detail cards view: what an
 * inspector shows for a row — display string + id on top, then ALL of the
 * entity's fields in labeled sections (Column Settings only customizes the
 * master-list cards, never this pane). Entity-specific inspectors (e.g.
 * LocationZonePlayerInspector) replace this wholesale.
 */
const props = withDefaults(
    defineProps<{
        row: Record<string, unknown>;
        /** Every column of the entity (visible or not) in table order. */
        columns: TableColumnStaticProps[];
        /** Column ids the header already shows. */
        excludeFields?: string[];
        entityId?: string;
    }>(),
    { excludeFields: () => [], entityId: '' }
);

/**
 * The host renders the record's identity (name + id) as the inspector's title, so
 * the field sections drop those: `display_string` always, and `name` unless it
 * differs from the displayed title (e.g. a track whose display string adds the
 * artist — there the plain name is still worth listing).
 */
const identityExcludedFields = computed(() => {
    const excluded = new Set([...props.excludeFields, 'display_string']);
    const display = props.row.display_string;
    const name = props.row.name;
    const displayIsMissing = typeof display !== 'string' || display.trim() === '';
    if (displayIsMissing || display === name) {
        excluded.add('name');
    }
    return [...excluded];
});

// Wherever RecordBody draws a record (its page, the side peek, the split pane)
// the person's chosen layout decides the groups. Anywhere else the built-in
// grouping stays.
const recordPage = useRecordPageContext();
const groups = computed(
    () =>
        recordPage?.groupsFor(props.columns, identityExcludedFields.value) ??
        groupColumnsForInspector(props.entityId, props.columns, identityExcludedFields.value)
);
</script>

<template>
    <div class="flex flex-col gap-4" data-testid="search-result-inspector">
        <SearchResultFieldSections :row="row" :groups="groups" :entity-id="entityId" />
    </div>
</template>
